import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const HelpPanelContext = createContext(null);

const STORAGE_KEY = 'help_panel_width';
const DEFAULT_WIDTH = 520;
const MIN_WIDTH = 400;
const MAX_WIDTH = 800;

// #3578 (TP-3578-1): Safari with Block All Cookies throws a SecurityError on any
// localStorage access. This provider wraps the whole app, so an unguarded read
// here sent every page to the error boundary before AuthContext's
// storage-blocked stop could render. Same shape as ThemeContext.readStoredTheme.
function readStoredPanelWidth() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

export function HelpPanelProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentSlug, setCurrentSlug] = useState('');
  const [scenarioContext, setScenarioContext] = useState(null);
  const [browsedToGuides, setBrowsedToGuides] = useState(false);
  // Issue #480: Coaching debrief context (evaluation, conversation, artifact in HelpPanel)
  const [coachingContext, setCoachingContext] = useState(null);
  const [coachingTab, setCoachingTab] = useState('evaluation');
  const [panelWidth, setPanelWidth] = useState(readStoredPanelWidth);

  // Prevent the URL-cleaning setSearchParams from re-triggering the effect
  const cleaningRef = useRef(false);

  // On mount + on location changes: consume ?help= param and seed state
  useEffect(() => {
    if (cleaningRef.current) {
      cleaningRef.current = false;
      return;
    }
    const helpParam = searchParams.get('help');
    if (helpParam !== null) {
      setIsOpen(true);
      setCurrentSlug(helpParam);
      // Clean URL — remove ?help= without adding a history entry
      cleaningRef.current = true;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('help');
        return next;
      }, { replace: true });
    }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close panel on logout (only on true → false transition, not initial false)
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true;
    } else if (wasAuthenticated.current) {
      // Was authenticated, now isn't — user logged out
      wasAuthenticated.current = false;
      setIsOpen(false);
      setCurrentSlug('');
    }
  }, [isAuthenticated]);

  const openHelp = useCallback((slug = '') => {
    setScenarioContext(null);
    setBrowsedToGuides(false);
    setIsOpen(true);
    setCurrentSlug(slug);
  }, []);

  const openScenarioContext = useCallback((runData) => {
    setCurrentSlug('');
    setBrowsedToGuides(false);
    setScenarioContext(runData);
    setIsOpen(true);
  }, []);

  // Issue #391: Toggle behavior for info button
  const toggleScenarioContext = useCallback((runData) => {
    // If closed, open with scenario context
    if (!isOpen) {
      setCurrentSlug('');
      setBrowsedToGuides(false);
      setScenarioContext(runData);
      setIsOpen(true);
      return;
    }

    // If showing scenario context, close panel
    const showingScenarioContext = !!scenarioContext && !currentSlug && !browsedToGuides;
    if (showingScenarioContext) {
      setIsOpen(false);
      setCurrentSlug('');
      setScenarioContext(null);
      setBrowsedToGuides(false);
      return;
    }

    // If showing guides/article, switch to scenario context
    setCurrentSlug('');
    setBrowsedToGuides(false);
    setScenarioContext(runData);
  }, [isOpen, scenarioContext, currentSlug, browsedToGuides]);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
    setCurrentSlug('');
    setScenarioContext(null);
    setBrowsedToGuides(false);
    setCoachingContext(null);
  }, []);

  // #4382: hide the panel WITHOUT clearing what it was opened with. HelpPanelHost
  // uses this when the panel's chunk fails to load, so the learner's scenario
  // context survives until a successful open (closeHelp would discard it).
  const dismissHelp = useCallback(() => {
    setIsOpen(false);
  }, []);

  const navigateToArticle = useCallback((slug) => {
    setCurrentSlug(slug);
  }, []);

  const navigateToGuides = useCallback(() => {
    setCurrentSlug('');
    setBrowsedToGuides(true);
  }, []);

  const navigateToScenarioInfo = useCallback(() => {
    setCurrentSlug('');
    setBrowsedToGuides(false);
  }, []);

  // Issue #480: Open HelpPanel with coaching reference data
  const openCoachingContext = useCallback((data) => {
    setCurrentSlug('');
    setScenarioContext(null);
    setBrowsedToGuides(false);
    setCoachingContext(data);
    setCoachingTab('evaluation');
    setIsOpen(true);
  }, []);

  const updateCoachingContext = useCallback((data) => {
    setCoachingContext(prev => prev ? { ...prev, ...data } : null);
  }, []);

  const savePanelWidth = useCallback((width) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
    setPanelWidth(clamped);
    try {
      localStorage.setItem(STORAGE_KEY, String(clamped));
    } catch {
      // Storage blocked (#3578, TP-3578-1): the width still applies for this page.
    }
  }, []);

  // Close panel on ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeHelp();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeHelp]);

  // Issue #391: Close panel and clear scenario context when navigating away from chat
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const wasChat = prevPathRef.current.startsWith('/chat/');
    const isChat = location.pathname.startsWith('/chat/');
    prevPathRef.current = location.pathname;
    if (wasChat && !isChat) {
      setIsOpen(false);
      setCurrentSlug('');
      setScenarioContext(null);
      setBrowsedToGuides(false);
      setCoachingContext(null);
    }
  }, [location.pathname]);

  return (
    <HelpPanelContext.Provider value={{
      isOpen,
      currentSlug,
      scenarioContext,
      browsedToGuides,
      openHelp,
      openScenarioContext,
      toggleScenarioContext,
      closeHelp,
      dismissHelp,
      navigateToArticle,
      navigateToGuides,
      navigateToScenarioInfo,
      panelWidth,
      savePanelWidth,
      MIN_WIDTH,
      MAX_WIDTH,
      coachingContext,
      coachingTab,
      openCoachingContext,
      updateCoachingContext,
      setCoachingTab,
    }}>
      {children}
    </HelpPanelContext.Provider>
  );
}

export function useHelpPanel() {
  const ctx = useContext(HelpPanelContext);
  if (!ctx) throw new Error('useHelpPanel must be used within HelpPanelProvider');
  return ctx;
}
